import * as angular from "angular";
import orderByDocumentPosition from "../../../filters/orderByDocumentPosition";
import tabbedModalTabs from "./tabbed-modal-tabs";
import { ModalTabHandle } from "./modalTabHandle";
import { ModalTabsCollection } from "./modalTabsCollection";
import template from "./tabbed-modal.html";
import "./tabbed-modal.less";
import type {
	TabbedModalBeforeChange,
	ModalTabItem,
	TabbedModalOnChange
} from "./tabbed-modal-types";
import type { UnsavedChangesSession } from "../../unsaved-changes/unsaved-changes-types";

const tabbedModalTransclude = {
	header: "tabbedModalHeader",
	body: "tabbedModalBody",
	footer: "tabbedModalFooter"
};

export interface TabbedModalController {
	readonly startOn?: string;
	beforeChange?: TabbedModalBeforeChange;
	onChange?: TabbedModalOnChange;
	readonly syncUrl?: boolean;
	session?: UnsavedChangesSession;
}

// AngularJS owns these merged binding properties; keeping them type-only preserves pre-assigned values.
// eslint-disable-next-line no-redeclare
export class TabbedModalController {
	public static $inject = ["$location", "$q"];

	public selected?: string;
	public readonly tabs: ModalTabsCollection;

	private urlSyncEnabled = true;

	public constructor(
		private $location: angular.ILocationService,
		private $q: angular.IQService
	) {
		this.tabs = new ModalTabsCollection(() => this.syncSelectedTab());
	}

	public $onInit(): void {
		this.urlSyncEnabled = this.syncUrl !== false;
	}

	public selectTab(tab: ModalTabHandle): angular.IPromise<void> {
		const nextTab = this.tabs.findSelectable(tab);
		if(!nextTab || nextTab.isSame(this.selected)) {
			return this.$q.when();
		}

		return this.canChangeTab(nextTab.tab).then(canChange => {
			if(!canChange) {
				return;
			}

			this.commitSelectedTab(nextTab, {
				notify: true,
				updateHash: this.urlSyncEnabled
			});
		});
	}

	private syncSelectedTab(): void {
		const nextTab = this.tabs.resolveTab([
			this.urlSyncEnabled ? this.$location.hash() : undefined,
			this.startOn,
			this.selected,
			this.tabs.firstSelectable?.code
		]);
		if(!nextTab) {
			return;
		}

		this.commitSelectedTab(nextTab, { notify: false, updateHash: false });
	}

	private canChangeTab(tab: ModalTabItem): angular.IPromise<boolean> {
		return this.$q.when(this.session?.checkIfDirty({ prompt: true })).then(confirmed => {
			if(confirmed === false) {
				return false;
			}

			return this.$q.when(this.beforeChange?.({
				tab,
				currentTab: this.tabs.findSelectableByCode(this.selected)?.tab
			})).then(result => result !== false);
		});
	}

	private commitSelectedTab(
		tab: ModalTabHandle,
		options: {
			notify: boolean;
			updateHash: boolean;
		}
	): void {
		this.selected = tab.code;

		if(options.updateHash) {
			this.updateHash(tab);
		}

		if(options.notify) {
			this.onChange?.({ selected: tab.code, tab: tab.tab });
		}
	}

	private updateHash(tab: ModalTabHandle): void {
		if(tab.hash) {
			this.$location.hash(tab.hash).replace();
		}
	}
}

export default angular
	.module("vesl.components.tabbed-modal.component", [
		orderByDocumentPosition,
		tabbedModalTabs
	])
	.component("tabbedModal", {
		template,
		transclude: tabbedModalTransclude,
		controller: TabbedModalController,
		bindings: {
			startOn: "<?selected",
			beforeChange: "&?",
			onChange: "&?",
			syncUrl: "<?",
			session: "=?"
		}
	})
	.name;
